// src/services/videoProjectService.js
//
// "My video" projects for the Video upload studio (Layout Builder).
//
// One document per topic at videoProjects/{toolKey} — the same key the
// Sing-along and Lesson video studios use for that topic:
//
// {
//   slug, title, topicId,
//   sourceUrl, sourcePath, sourceName,     // the video YOU uploaded
//   duration,                              // seconds
//   offset,                                // video time where the song's 0:00 is
//   showLyrics, showLogo,
//   overlays: [{ id, from, to, url, path, fit, caption, prompt, still,
//                x, y, w, h, frame }],        // x/y/w/h only for fit "custom"
//   shapes:   [{ id, kind, x, y, w, h, fill, opacity, mode, always, from, to }],
//              // cover-ups and drawings on top of your video; x/y/w/h are
//              // fractions of the frame (0–1) so 720p and 1080p match
//   outputUrl, outputPath, outputSig, outputMadeAt,   // the finished MP4
//   updatedAt, updatedBy
// }
//
// Its own collection on purpose: nothing here is written into lyricSync,
// lessonStoryboards or topicLibrary, so this studio can't change a
// sing-along, an animated lesson video or a topic — except the two
// "Use as …" buttons, which write ONE field on the topic on purpose.
//
// firestore.rules: videoProjects is admin-only in both directions.

import { collection, doc, getDocs, serverTimestamp, setDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebase/firebaseConfig";
import { exactKey } from "../utils/topicKey";
import { syncSignature } from "./lyricSyncService";

export const VIDEO_PROJECTS = "videoProjects";

const round2 = (n) => Math.round(Number(n) * 100) / 100;
const round4 = (n) => Math.round(Number(n) * 10000) / 10000;
const frac = (n, dflt) => {
  const v = Number(n);
  return Number.isFinite(v) ? round4(Math.max(0, Math.min(1, v))) : dflt;
};
const hex = (c) => (/^#[0-9a-f]{6}$/i.test(String(c || "")) ? String(c).toLowerCase() : "#000000");

export const SHAPE_KINDS = ["rect", "round", "ellipse"];

/** Shapes with clean numbers; ones with no size dropped. */
export const cleanShapes = (shapes) =>
  (Array.isArray(shapes) ? shapes : [])
    .map((s) => ({
      id: String(s.id || Math.random().toString(36).slice(2, 10)),
      kind: SHAPE_KINDS.includes(s.kind) ? s.kind : "rect",
      x: frac(s.x, 0.4),
      y: frac(s.y, 0.4),
      w: frac(s.w, 0.2),
      h: frac(s.h, 0.1),
      fill: hex(s.fill),
      opacity: frac(s.opacity ?? 1, 1),
      mode: s.mode === "blur" ? "blur" : "color",
      always: s.always !== false,
      from: round2(Math.max(0, Number(s.from) || 0)),
      to: round2(Math.max(0, Number(s.to) || 0)),
    }))
    .filter((s) => s.w > 0.002 && s.h > 0.002);

export const getAllVideoProjects = async () => {
  const snap = await getDocs(collection(db, VIDEO_PROJECTS));
  const out = {};
  snap.forEach((d) => {
    out[d.id] = { id: d.id, ...d.data() };
  });
  return out;
};

/** Picture clips with clean numbers, sorted by start; bad ones dropped. */
export const cleanOverlays = (overlays) =>
  (Array.isArray(overlays) ? overlays : [])
    .map((o) => ({
      id: String(o.id || Math.random().toString(36).slice(2, 10)),
      from: round2(Math.max(0, Number(o.from) || 0)),
      to: round2(Math.max(0, Number(o.to) || 0)),
      url: String(o.url || ""),
      path: String(o.path || ""),
      fit: String(o.fit || "board"),
      caption: String(o.caption || ""),
      prompt: String(o.prompt || ""),
      still: Boolean(o.still),
      x: frac(o.x, 0.3),
      y: frac(o.y, 0.2),
      w: frac(o.w, 0.4),
      h: frac(o.h, 0.4),
      frame: Boolean(o.frame),
    }))
    .filter((o) => o.to > o.from)
    .sort((a, b) => a.from - b.from);

/** Merges, so fields this caller did not send survive. */
export const saveVideoProject = async (slug, data) => {
  const id = exactKey(slug);
  if (!id) throw new Error("A video project needs a topic slug.");
  const user = getAuth().currentUser;
  const payload = {
    ...data,
    slug: id,
    updatedAt: serverTimestamp(),
    updatedBy: user?.email || user?.uid || "unknown",
  };
  if (payload.overlays) payload.overlays = cleanOverlays(payload.overlays);
  if (payload.shapes) payload.shapes = cleanShapes(payload.shapes);
  if (payload.offset !== undefined) payload.offset = round2(payload.offset || 0);
  await setDoc(doc(db, VIDEO_PROJECTS, id), payload, { merge: true });
  return payload;
};

/**
 * Fingerprint of everything that changes the finished MP4, to tell
 * "Video ✓" from "Video out of date".
 */
export const projectSignature = (project, sync) => {
  const parts = [
    project?.sourcePath || project?.sourceUrl || "",
    round2(project?.offset || 0),
    project?.showLyrics !== false,
    project?.showLogo !== false,
    cleanOverlays(project?.overlays).map((o) =>
      o.fit === "custom"
        ? [o.from, o.to, o.url, o.fit, o.caption, o.still, o.x, o.y, o.w, o.h, o.frame]
        : [o.from, o.to, o.url, o.fit, o.caption, o.still]
    ),
    // only added when there are shapes, so videos made before shapes existed
    // keep their "up to date" status
    ...(cleanShapes(project?.shapes).length
      ? [cleanShapes(project.shapes).map((s) => [s.kind, s.x, s.y, s.w, s.h, s.fill, s.opacity, s.mode, s.always, s.from, s.to])]
      : []),
    project?.showLyrics !== false ? syncSignature({ lines: sync?.lines, cues: sync?.cues }) : "",
    // the topic's song / sound levels — only added when used, so videos made
    // before them keep their "up to date" status
    ...(project?.songOn ? [["song", Number(project.songVolume ?? 1)]] : []),
    // set only when the Topic Library's song replaced the one the lyrics were
    // timed to (VideoUploadPanel) — so older videos keep their status
    ...(project?.songOn && project?.songFile ? [["songFile", String(project.songFile)]] : []),
    ...(project?.videoVolume !== undefined && Number(project.videoVolume) !== 1 ? [["videoVolume", Number(project.videoVolume)]] : []),
  ];
  const text = JSON.stringify(parts);
  let h = 5381;
  for (let i = 0; i < text.length; i += 1) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};
